import React from "react";
import { dialog } from "../../components/ui/Dialog";
import {
  ajouterDocumentApi, ajouterSourceApi, basculerFavoriApi, chargerKb, creerDossierApi, modifierDossierApi,
  supprimerDossierApi, supprimerElementApi, validerDossierApi,
} from "./kbApi";

// Dernier état chargé : en revenant sur l'écran il s'affiche tout de suite,
// pendant que la version à jour est relue.
let cacheKb = null;

// Champs du frontend -> champs de l'API.
const VERS_API = { title: "titre", domain: "domaine", status: "statut", tags: "tags", synthese: "synthese", clients: "clients" };

// Magasin de la Base de connaissance, branché sur l'API. Le serveur décide de
// la levée de validation et renvoie le dossier à jour plus les lignes de
// journal créées ; l'écran n'a qu'à les fusionner.
export const useKbStore = () => {
  const [db, setDb] = React.useState(() => cacheKb || { folders: [], log: [], favs: [] });
  const [chargement, setChargement] = React.useState(!cacheKb);
  const [erreur, setErreur] = React.useState(null);

  const miseAJour = (fn) =>
    setDb((d) => {
      const suivant = fn(d);
      cacheKb = suivant;
      return suivant;
    });

  const recharger = React.useCallback(async () => {
    try {
      const data = await chargerKb();
      const suivant = { folders: data.dossiers, log: data.journal, favs: data.favoris };
      cacheKb = suivant;
      setDb(suivant);
      setErreur(null);
    } catch (e) {
      console.error("Base de connaissance — chargement :", e);
      setErreur(e.message || "Chargement impossible.");
    } finally {
      setChargement(false);
    }
  }, []);

  React.useEffect(() => {
    recharger();
  }, [recharger]);

  const appliquer = ({ dossier, evenements }) =>
    miseAJour((d) => ({
      ...d,
      folders: d.folders.some((f) => f.id === dossier.id)
        ? d.folders.map((f) => (f.id === dossier.id ? dossier : f))
        : [dossier, ...d.folders],
      log: [...(evenements || []), ...d.log],
    }));

  // Une action refusée ou en échec est signalée sans casser l'écran.
  const executer = async (fn) => {
    try {
      return await fn();
    } catch (e) {
      console.error("Base de connaissance :", e);
      await dialog.alert(e.message || "L'opération a échoué.");
      return null;
    }
  };

  return [
    db,
    {
      async create({ title, domain, tags }) {
        const r = await executer(() => creerDossierApi({ titre: title, domaine: domain, tags }));
        if (!r) return null;
        appliquer(r);
        return r.dossier.id;
      },
      // patch : { title, domain, status, tags, synthese, clients } (un ou plusieurs).
      async update(id, patch) {
        const body = {};
        Object.keys(patch).forEach((k) => {
          if (VERS_API[k]) body[VERS_API[k]] = patch[k];
        });
        const r = await executer(() => modifierDossierApi(id, body));
        if (r) appliquer(r);
        return !!r;
      },
      async validate(id, on) {
        const r = await executer(() => validerDossierApi(id, on));
        if (r) appliquer(r);
        return !!r;
      },
      async addSource(id, { title, url }) {
        const r = await executer(() => ajouterSourceApi(id, { titre: title, url }));
        if (r) appliquer(r);
        return !!r;
      },
      async addDocument(id, doc) {
        const r = await ajouterDocumentApi(id, doc);
        appliquer(r);
        return r;
      },
      async delItem(id, iid) {
        const r = await executer(() => supprimerElementApi(iid));
        if (r) appliquer(r);
        return !!r;
      },
      async toggleFav(id) {
        const etait = db.favs.includes(id);
        miseAJour((d) => ({ ...d, favs: etait ? d.favs.filter((x) => x !== id) : [...d.favs, id] }));
        try {
          await basculerFavoriApi(id);
        } catch (e) {
          miseAJour((d) => ({ ...d, favs: etait ? [...d.favs, id] : d.favs.filter((x) => x !== id) }));
          await dialog.alert(e.message || "Le favori n'a pas pu être enregistré.");
        }
      },
      async remove(id) {
        const r = await executer(() => supprimerDossierApi(id));
        if (!r) return false;
        miseAJour((d) => ({
          folders: d.folders.filter((f) => f.id !== id),
          favs: d.favs.filter((x) => x !== id),
          log: [...r.evenements, ...d.log],
        }));
        return true;
      },
    },
    { chargement, erreur, recharger },
  ];
};
